import { Cesium3DTileset, Model, Transforms, createGuid } from "@cesium/engine";
import DitapViewer from "./DitapViewer";
import DitapAlert from "../popup/DitapAlert";

/**
 * 3D 데이터 입력 기능 클래스. URL을 통해 객체 생성 후 뷰어 표출.<br>
 * Function class for 3D data entry. Create an object via URL and display it in the viewer.
 *
 * @alias Input3DData
 * @constructor
 *
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {DitapViewer} id 3D 데이터에 할당할 ID.<br> ID to assign to the 3D data.
 */
class Input3DData {
  constructor(viewer, id) {
    if (id) {
      this._id = id;
    } else {
      this._id = undefined;
    }
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      viewer.ditapStorage.input3DModel[this._id] = this;
    } else {
      console.error("Type doesn't match");
    }
    this._inputModel = undefined;
    this._position = undefined;
    this._url = undefined;
    this._type = undefined;
    this._originalStyle = undefined;
  }

  /**
   * 3D 데이터에 할당할 ID.<br>
   * ID to assign to the 3D data.
   * @type {string}
   */
  set id(id) {
    this._id = id;
  }
  get id() {
    return this._id;
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }
  get viewer() {
    return this._viewer;
  }

  /**
   * 3D 데이터가 업로드 될 위치.<br>
   * Where the 3D data will be uploaded.
   * @type {Cartesian3}
   */
  set position(position) {
    this._position = position;
  }
  get position() {
    return this._position;
  }

  /**
   * 업로드 할 데이터 URL.<br>
   * URL of the data to upload.
   * @type {string}
   */
  set url(url) {
    this._url = url;
  }
  get url() {
    return this._url;
  }

  /**
   * 업로드할 데이터 유형.<br>
   * Type of upload data.
   * @type {string}
   */
  set type(type) {
    this._type = type;
  }
  get type() {
    return this._type;
  }

  /**
   * 인스턴스 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the instance. Should be called if permanently removing the instance from layout.
   */
  destroy() {
    this.id = undefined;
    this.position = undefined;
    this.url = undefined;
    this.type = undefined;
  }

  /**
   * 특정 위치에 모델 생성.</br>
   * Create a model in a specific location.
   * @param {Cartesian3} position 모델을 생성할 좌표. <br> Cartesian3 to create a model.
   */
  async createModel(position) {
    try {
      // 모델 랜더링
      this.id = `input3DModel${createGuid()}`;
      const modelMatrix = Transforms.eastNorthUpToFixedFrame(position);
      const model = await Model.fromGltfAsync({
        id: this.id,
        url: this.url,
        modelMatrix: modelMatrix,
      });
      this.inputModel = model;
      this.viewer.scene.primitives.add(model);
    } catch (error) {
      const warningAlert = new DitapAlert({
        type: "warning",
        title: "데이터 추가",
        content1: "모델 로드에 실패하였습니다.",
        content2: "올바른 URL을 다시 입력해주세요.",
        buttons: "ONLYOK",
        open: false,
        callback: (alert) => {
          alert.destroy();
        },
      });
      warningAlert.open();
    }
  }

  /**
   * Ditap Viewer에 타일셋 생성.</br>
   * Create a tileset in the DitapViewer.
   * @param {Function} callback 타일셋 생성 후 실행할 함수. </br> After creating the tile set, execute the function.
   */
  async createTileset(callback) {
    try {
      this.id = `input3DTileset${createGuid()}`;
      const tileset = await Cesium3DTileset.fromUrl(this.url);

      tileset.id = this.id; // id 속성 추가
      this.viewer.scene.primitives.add(tileset);
      this.inputModel = tileset;
      // 카메라 이동
      this.viewer.camera.flyToBoundingSphere(tileset.boundingSphere, {
        duration: 2,
      });

      if (callback) {
        callback();
      }
    } catch (error) {
      const warningAlert = new DitapAlert({
        type: "warning",
        title: "데이터 추가",
        content1: "유효하지 않은 3D Tiles URL 입니다.",
        content2: "올바른 URL을 다시 입력해주세요.",
        buttons: "ONLYOK",
        open: false,
        callback: (alert) => {
          alert.destroy();
        },
      });
      warningAlert.open();
    }
  }
}

export default Input3DData;
